import { useEffect, useState, useSyncExternalStore } from 'react';
import { Button, Modal, Tabs } from '../../components/primitives';
import { cx } from '../../lib/cx';
import {
  canPromptInstall,
  detectPlatform,
  isStandalone,
  promptInstall,
  subscribeInstallPrompt,
  type InstallPlatform,
} from '../../lib/installPrompt';
import styles from './InstallGuide.module.css';

/**
 * Animovaný návod „Jak nainstalovat SiteDoc jako aplikaci (PWA)".
 * Žádné video — makety telefonu/prohlížeče v CSS, kroky se samy přehrávají
 * dokola. Kde prohlížeč nabízí nativní instalaci (Chrome/Edge/Android),
 * je navíc tlačítko „Nainstalovat teď".
 */

interface Guide {
  label: string;
  steps: { title: string; text: string }[];
}

const GUIDES: Record<InstallPlatform, Guide> = {
  ios: {
    label: 'iPhone / iPad',
    steps: [
      {
        title: 'Otevřete sitedoc.cz v Safari',
        text: 'Klepněte na tlačítko Sdílet (čtvereček se šipkou). V novějším iOS bývá schované pod „•••".',
      },
      { title: 'Zvolte „Přidat na plochu"', text: 'V nabídce sdílení sjeďte níž, pokud položku hned nevidíte.' },
      { title: 'Potvrďte „Přidat"', text: 'Název SiteDoc můžete nechat, nebo přepsat.' },
      { title: 'Hotovo', text: 'Ikona SiteDoc je na ploše a otevírá se jako samostatná aplikace.' },
    ],
  },
  android: {
    label: 'Android',
    steps: [
      { title: 'Otevřete sitedoc.cz v Chrome', text: 'Klepněte na tři tečky ⋮ vpravo nahoře.' },
      {
        title: 'Zvolte „Instalovat aplikaci"',
        text: 'Na některých telefonech se položka jmenuje „Přidat na plochu".',
      },
      { title: 'Potvrďte „Instalovat"', text: 'Chrome aplikaci přidá mezi ostatní aplikace i na plochu.' },
      { title: 'Hotovo', text: 'SiteDoc spustíte z plochy jako každou jinou aplikaci.' },
    ],
  },
  desktop: {
    label: 'Počítač',
    steps: [
      {
        title: 'Otevřete sitedoc.cz v Chrome nebo Edge',
        text: 'Vpravo v adresním řádku klikněte na ikonu instalace (monitor se šipkou).',
      },
      {
        title: 'Klikněte „Instalovat"',
        text: 'V Edge případně ⋯ → Aplikace → Nainstalovat tento web jako aplikaci.',
      },
      { title: 'Hotovo', text: 'SiteDoc běží ve vlastním okně a najdete ho v nabídce Start i na liště.' },
    ],
  },
};

const STEP_MS = 2600;
const LAST_STEP_MS = 3400;

const Tap = () => <span className={styles.tap} aria-hidden />;

export function InstallGuideButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  if (typeof window !== 'undefined' && isStandalone()) return null;
  return (
    <>
      <button type="button" className={cx(styles.trigger, className)} onClick={() => setOpen(true)}>
        <PhoneIcon /> Jak nainstalovat jako aplikaci
      </button>
      <InstallGuideModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function InstallGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [platform, setPlatform] = useState<InstallPlatform>(() => detectPlatform());
  const [step, setStep] = useState(0);
  const nativeAvailable = useSyncExternalStore(subscribeInstallPrompt, canPromptInstall, () => false);
  const guide = GUIDES[platform];
  const last = guide.steps.length - 1;

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setStep((s) => (s >= last ? 0 : s + 1)), step >= last ? LAST_STEP_MS : STEP_MS);
    return () => window.clearTimeout(t);
  }, [open, step, last]);

  const changePlatform = (p: InstallPlatform) => {
    setPlatform(p);
    setStep(0);
  };

  return (
    <Modal open={open} onClose={onClose} title="Instalace SiteDoc jako aplikace" size="lg">
      <div className={styles.head}>
        <Tabs
          items={(Object.keys(GUIDES) as InstallPlatform[]).map((id) => ({ id, label: GUIDES[id].label }))}
          value={platform}
          onChange={changePlatform}
          ariaLabel="Zařízení"
        />
        {nativeAvailable && (
          <Button variant="primary" onClick={() => void promptInstall().then((ok) => ok && onClose())}>
            Nainstalovat teď
          </Button>
        )}
      </div>

      <div className={styles.layout}>
        <div className={styles.stage} aria-hidden>
          {platform === 'ios' && <IosScene step={step} />}
          {platform === 'android' && <AndroidScene step={step} />}
          {platform === 'desktop' && <DesktopScene step={step} />}
        </div>

        <ol className={styles.steps}>
          {guide.steps.map((s, i) => (
            <li key={s.title}>
              <button
                type="button"
                className={cx(styles.step, i === step && styles.stepActive, i < step && styles.stepDone)}
                onClick={() => setStep(i)}
              >
                <span className={styles.stepNo}>{i === last ? '✓' : i + 1}</span>
                <span className={styles.stepBody}>
                  <span className={styles.stepTitle}>{s.title}</span>
                  <span className={styles.stepText}>{s.text}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <p className={styles.note}>
        Aplikace se nestahuje z obchodu — je to stejný SiteDoc jako v prohlížeči, jen s vlastní ikonou a
        oknem. Aktualizace přicházejí automaticky. Na iPhonu fungují upozornění jen v nainstalované aplikaci.
      </p>
    </Modal>
  );
}

/* ───────────── makety ───────────── */

function MiniPage() {
  return (
    <div className={styles.miniPage}>
      <div className={styles.miniLogo}>S</div>
      <div className={styles.miniLine} style={{ width: '70%' }} />
      <div className={cx(styles.miniLine, styles.miniAccent)} style={{ width: '50%' }} />
      <div className={styles.miniLine} style={{ width: '85%', opacity: 0.5 }} />
      <div className={styles.miniLine} style={{ width: '75%', opacity: 0.5 }} />
      <div className={styles.miniBtn} />
      <div className={styles.miniTiles}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={styles.miniTile} />
        ))}
      </div>
    </div>
  );
}

function HomeScreen({ show, rounded }: { show: boolean; rounded: 'ios' | 'android' }) {
  return (
    <div className={cx(styles.home, show && styles.homeShow)}>
      <div className={styles.homeGrid}>
        {Array.from({ length: 11 }, (_, i) => (
          <div key={i} className={styles.homeApp}>
            <div className={cx(styles.homeIcon, rounded === 'android' && styles.homeIconRound)} />
          </div>
        ))}
        <div className={styles.homeApp}>
          {show && (
            <>
              <div className={cx(styles.homeIcon, styles.appIcon, styles.pop, rounded === 'android' && styles.homeIconRound)}>
                S
              </div>
              <span className={styles.homeLabel}>SiteDoc</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function IosScene({ step }: { step: number }) {
  return (
    <div className={styles.phone}>
      <div className={styles.screen}>
        <div className={styles.statusBar}>
          <span>9:41</span>
          <span>●●● ▮</span>
        </div>
        <MiniPage />
        <div className={styles.iosBar}>
          <div className={styles.iosUrl}>sitedoc.cz</div>
          <div className={styles.iosTools}>
            <span>‹</span>
            <span>›</span>
            <span className={cx(styles.iconHit, step === 0 && styles.hot)}>
              <ShareIcon />
              {step === 0 && <Tap />}
            </span>
            <span>▢</span>
            <span>⧉</span>
          </div>
        </div>

        <div className={cx(styles.sheet, step === 1 && styles.sheetOpen)}>
          <div className={styles.sheetGrip} />
          <div className={styles.sheetRow}>Kopírovat</div>
          <div className={styles.sheetRow}>Přidat do záložek</div>
          <div className={cx(styles.sheetRow, step === 1 && styles.rowHot)}>
            Přidat na plochu <PlusSquareIcon />
            {step === 1 && <Tap />}
          </div>
          <div className={styles.sheetRow}>Najít na stránce</div>
        </div>

        <div className={cx(styles.addPanel, step === 2 && styles.addPanelOpen)}>
          <div className={styles.addHead}>
            <span className={styles.addLink}>Zrušit</span>
            <strong>Přidat na plochu</strong>
            <span className={cx(styles.addLink, styles.addLinkBold, step === 2 && styles.hotText)}>
              Přidat
              {step === 2 && <Tap />}
            </span>
          </div>
          <div className={styles.addCard}>
            <div className={cx(styles.homeIcon, styles.appIcon)}>S</div>
            <div>
              <div className={styles.addName}>SiteDoc</div>
              <div className={styles.addUrl}>https://sitedoc.cz</div>
            </div>
          </div>
        </div>

        <HomeScreen show={step === 3} rounded="ios" />
      </div>
    </div>
  );
}

function AndroidScene({ step }: { step: number }) {
  return (
    <div className={styles.phone}>
      <div className={styles.screen}>
        <div className={styles.statusBar}>
          <span>9:41</span>
          <span>▾ ▮</span>
        </div>
        <div className={styles.chromeBar}>
          <div className={styles.chromeUrl}>🔒 sitedoc.cz</div>
          <span className={cx(styles.iconHit, step === 0 && styles.hot)}>
            ⋮{step === 0 && <Tap />}
          </span>
        </div>
        <MiniPage />

        <div className={cx(styles.menu, step === 1 && styles.menuOpen)}>
          <div className={styles.menuRow}>Nová karta</div>
          <div className={styles.menuRow}>Historie</div>
          <div className={styles.menuRow}>Stažené soubory</div>
          <div className={styles.menuRow}>Sdílet…</div>
          <div className={cx(styles.menuRow, step === 1 && styles.rowHot)}>
            Instalovat aplikaci
            {step === 1 && <Tap />}
          </div>
          <div className={styles.menuRow}>Nastavení</div>
        </div>

        <div className={cx(styles.scrim, step === 2 && styles.scrimShow)}>
          <div className={styles.dialog}>
            <div className={styles.dialogTitle}>Instalovat aplikaci</div>
            <div className={styles.addCard}>
              <div className={cx(styles.homeIcon, styles.appIcon, styles.homeIconRound)}>S</div>
              <div>
                <div className={styles.addName}>SiteDoc</div>
                <div className={styles.addUrl}>sitedoc.cz</div>
              </div>
            </div>
            <div className={styles.dialogBtns}>
              <span>Zrušit</span>
              <span className={cx(styles.dialogPrimary, step === 2 && styles.hotText)}>
                Instalovat
                {step === 2 && <Tap />}
              </span>
            </div>
          </div>
        </div>

        <HomeScreen show={step === 3} rounded="android" />
      </div>
    </div>
  );
}

function DesktopScene({ step }: { step: number }) {
  const installed = step === 2;
  return (
    <div className={styles.desktop}>
      <div className={styles.window}>
        <div className={styles.titleBar}>
          {installed ? (
            <span className={styles.appTitle}>
              <span className={styles.appTitleIcon}>S</span> SiteDoc — Stavební dokumentace
            </span>
          ) : (
            <span className={styles.browserTab}>SiteDoc</span>
          )}
          <span className={styles.winBtns}>— ▢ ✕</span>
        </div>
        {!installed && (
          <div className={styles.addrBar}>
            <span className={styles.addrNav}>‹ › ↻</span>
            <div className={styles.addrUrl}>
              <span>🔒 sitedoc.cz</span>
              <span className={cx(styles.iconHit, step === 0 && styles.hot)}>
                <InstallIcon />
                {step === 0 && <Tap />}
              </span>
            </div>
          </div>
        )}
        <div className={styles.winBody}>
          <MiniPage />
        </div>

        <div className={cx(styles.popover, step === 1 && styles.popoverOpen)}>
          <div className={styles.dialogTitle}>Instalovat aplikaci?</div>
          <div className={styles.addCard}>
            <div className={cx(styles.homeIcon, styles.appIcon)}>S</div>
            <div>
              <div className={styles.addName}>SiteDoc</div>
              <div className={styles.addUrl}>sitedoc.cz</div>
            </div>
          </div>
          <div className={styles.dialogBtns}>
            <span className={cx(styles.pcPrimary, step === 1 && styles.hotText)}>
              Instalovat
              {step === 1 && <Tap />}
            </span>
            <span className={styles.pcSecondary}>Zrušit</span>
          </div>
        </div>
      </div>

      <div className={styles.taskbar}>
        <span className={styles.taskIcon}>⊞</span>
        <span className={styles.taskIcon} />
        <span className={styles.taskIcon} />
        {installed && <span className={cx(styles.taskIcon, styles.appIcon, styles.pop)}>S</span>}
      </div>
    </div>
  );
}

/* ───────────── ikony ───────────── */

function ShareIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M7 1v9M4 4l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 6.5H2v8.5h10V6.5H9.5" strokeLinejoin="round" />
    </svg>
  );
}

function PlusSquareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1" y="1" width="12" height="12" rx="3" />
      <path d="M7 4v6M4 7h6" strokeLinecap="round" />
    </svg>
  );
}

function InstallIcon() {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1" y="1" width="14" height="9" rx="1.5" />
      <path d="M5 13h6M8 3.5v4M6 6l2 2 2-2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="2" y="1" width="10" height="14" rx="2" />
      <path d="M7 4.5v5M5 7.5l2 2 2-2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
